import React, { createContext, PropsWithChildren, useEffect, useState } from 'react';
import { useToggle } from 'react-use';
import { RecordingStatus } from '@zoom/videosdk';

import RequestInterpreter from 'widgets/zoom/RequestInterpreter';
import Room from 'widgets/zoom/Room';
import Settings from 'widgets/zoom/Settings';

import { useExternalDeviceIndicator } from 'hooks/useExternalDeviceIndicator';

import { ZoomCallContextProps } from './zoomCallContext.types';

const ZOOM_SDK_ERROR_TYPES = new Set([
  'IMPROPER_MEETING_STATE',
  'INVALID_OPERATION',
  'OPERATION_TIMEOUT'
]);

const isZoomSdkError = (reason: unknown): boolean =>
  typeof reason === 'object' &&
  reason !== null &&
  'type' in reason &&
  'errorCode' in reason &&
  ZOOM_SDK_ERROR_TYPES.has((reason as { type: string }).type);

const defaultValue: ZoomCallContextProps = {
  appointment: null,
  audioEnabled: true,
  callOutParticipant: null,
  cameraId: '',
  isEnableCaptions: false,
  isMinimize: false,
  isOpenChat: false,
  isOpenRequestInterpreter: false,
  isOpenSettings: false,
  languageRequested: null,
  loadingRoom: false,
  loggerClient: null,
  microphoneId: '',
  resetValuesToDefault: () => {},
  room: null,
  setAppointment: () => {},
  setCallOutParticipant: () => {},
  setCameraId: () => {},
  setLanguageRequested: () => {},
  setLoggerClient: () => {},
  setMicrophoneId: () => {},
  setRoom: () => {},
  setShowControls: () => {},
  setSpeakerId: () => {},
  showControls: true,
  showDeviceIndicator: false,
  speakerId: '',
  toggleAudioEnabled: () => {},
  toggleIsEnableCaptions: () => {},
  toggleIsMinimize: () => {},
  toggleIsOpenChat: () => {},
  toggleIsOpenRequestInterpreter: () => {},
  toggleIsOpenSettings: () => {},
  toggleLoadingRoom: () => {},
  toggleVideoEnabled: () => {},
  videoEnabled: true
};

export const ZoomCallContext = createContext<ZoomCallContextProps>(defaultValue);

export const ZoomCallProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [room, setRoom] = useState(defaultValue.room);
  const [appointment, setAppointment] = useState(defaultValue.appointment);
  const [callOutParticipant, setCallOutParticipant] = useState(defaultValue.callOutParticipant);
  const [loggerClient, setLoggerClient] = useState(defaultValue.loggerClient);
  const [cameraId, setCameraId] = useState(defaultValue.cameraId);
  const [microphoneId, setMicrophoneId] = useState(defaultValue.microphoneId);
  const [speakerId, setSpeakerId] = useState(defaultValue.speakerId);
  const [languageRequested, setLanguageRequested] = useState(defaultValue.languageRequested);

  const [isOpenChat, toggleIsOpenChat] = useToggle(defaultValue.isOpenChat);
  const [loadingRoom, toggleLoadingRoom] = useToggle(defaultValue.loadingRoom);
  const [audioEnabled, toggleAudioEnabled] = useToggle(defaultValue.audioEnabled);
  const [videoEnabled, toggleVideoEnabled] = useToggle(defaultValue.videoEnabled);
  const [isMinimize, toggleIsMinimize] = useToggle(defaultValue.isMinimize);
  const [showControls, setShowControls] = useToggle(defaultValue.showControls);
  const [isOpenSettings, toggleIsOpenSettings] = useToggle(defaultValue.isOpenSettings);
  const [isOpenRequestInterpreter, toggleIsOpenRequestInterpreter] = useToggle(
    defaultValue.isOpenRequestInterpreter
  );
  const [isEnableCaptions, toggleIsEnableCaptions] = useToggle(defaultValue.isEnableCaptions);

  const showDeviceIndicator = useExternalDeviceIndicator(isOpenSettings);

  useEffect(() => {
    const suppressZoomSdkRejections = (event: PromiseRejectionEvent) => {
      if (isZoomSdkError(event.reason)) {
        event.preventDefault();
      }
    };

    window.addEventListener('unhandledrejection', suppressZoomSdkRejections);
    return () => window.removeEventListener('unhandledrejection', suppressZoomSdkRejections);
  }, []);

  const resetValuesToDefault = () => {
    const client = room?.getRecordingClient();
    Promise.all([
      ...(client?.getCloudRecordingStatus() === RecordingStatus.Recording
        ? [client.stopCloudRecording().catch(() => {})]
        : []),
      room?.leave().catch(() => {})
    ]).finally(() => {
      setRoom(defaultValue.room);
      setAppointment(defaultValue.appointment);
      setCallOutParticipant(defaultValue.callOutParticipant);
      setLoggerClient(defaultValue.loggerClient);
      setCameraId(defaultValue.cameraId);
      setMicrophoneId(defaultValue.microphoneId);
      setSpeakerId(defaultValue.speakerId);
      setLanguageRequested(defaultValue.languageRequested);
      toggleIsOpenChat(defaultValue.isOpenChat);
      toggleLoadingRoom(defaultValue.loadingRoom);
      toggleAudioEnabled(defaultValue.audioEnabled);
      toggleVideoEnabled(defaultValue.videoEnabled);
      toggleIsMinimize(defaultValue.isMinimize);
      setShowControls(defaultValue.showControls);
      toggleIsOpenSettings(defaultValue.isOpenSettings);
      toggleIsOpenRequestInterpreter(defaultValue.isOpenRequestInterpreter);
      toggleIsEnableCaptions(defaultValue.isEnableCaptions);
    });
  };

  return (
    <ZoomCallContext.Provider
      value={{
        appointment,
        audioEnabled,
        callOutParticipant,
        cameraId,
        isEnableCaptions,
        isMinimize,
        isOpenChat,
        isOpenRequestInterpreter,
        isOpenSettings,
        languageRequested,
        loadingRoom,
        loggerClient,
        microphoneId,
        resetValuesToDefault,
        room,
        setAppointment,
        setCallOutParticipant,
        setCameraId,
        setLanguageRequested,
        setLoggerClient,
        setMicrophoneId,
        setRoom,
        setShowControls,
        setSpeakerId,
        showControls,
        showDeviceIndicator,
        speakerId,
        toggleAudioEnabled,
        toggleIsEnableCaptions,
        toggleIsMinimize,
        toggleIsOpenChat,
        toggleIsOpenRequestInterpreter,
        toggleIsOpenSettings,
        toggleLoadingRoom,
        toggleVideoEnabled,
        videoEnabled
      }}
    >
      <Settings />
      <RequestInterpreter />
      <Room />
      {children}
    </ZoomCallContext.Provider>
  );
};
